
import React, { useState } from 'react';
import { SalesLead, ExpenseTransaction, ExpenseCategory, ExpenseStatus, Budget, SalesActivity, LeadStage, SalesTarget, Contractor, ContractorDocument, ContractorEmployee, PerformanceScore, ContractorGrade, DocType, Employee, LeaveRequest, OvertimeEntry, Competency, PPEItem, PerformanceReview, HRDepartment, LeaveType, ScorecardItem, PDPGoal, Attachment, NextOfKin } from '../../types';
import { 
  Briefcase, UserPlus, DollarSign, Receipt, FileText, CheckSquare, Users, 
  TrendingUp, CreditCard, PieChart, AlertTriangle, CheckCircle, XCircle, 
  Filter, Download, Camera, Plus, Search, Building2, Wallet, Landmark,
  Phone, Mail, Calendar, Clock, ArrowRight, User, MoreVertical, X, MapPin, Target,
  Shield, Award, BarChart2, Star, BookOpen, Upload, FileCheck, HardHat, GraduationCap, Layout, ArrowLeft, Link as LinkIcon, Paperclip, Trash2, HeartPulse, Truck, Ruler, Lock
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart as RePieChart, Pie, Cell, AreaChart, Area, LineChart, Line
} from 'recharts';

// --- MOCK DATA ---

const initialLeads: SalesLead[] = [
  { 
      id: 'LD-101', company: 'Defy Appliances', contactPerson: 'Mike Ross', email: 'm.ross@defy.co.za', phone: '082 555 1010',
      value: 450000, stage: 'Negotiation', owner: 'M. Scott', probability: 80,
      source: 'Referral', region: 'DBN', temperature: 'Hot', nextAction: 'Finalize Contract Terms', nextActionDate: '2024-05-25',
      activities: [
          { id: 'ACT-001', type: 'Call', date: '2024-05-20', notes: 'Discussed pricing revisions', loggedBy: 'M. Scott', outcome: 'Positive' },
          { id: 'ACT-002', type: 'Site Visit', date: '2024-05-10', notes: 'Inspected plant room for install space', loggedBy: 'M. Scott', outcome: 'Requirements Confirmed' }
      ], productMix: { equipment: 400000, service: 50000, spares: 0 }, gpMargin: 25
  },
  { 
      id: 'LD-105', company: 'Illovo Sugar', contactPerson: 'Sarah Jee', email: 's.jee@illovo.co.za', phone: '083 444 9922',
      value: 120000, stage: 'Quote', owner: 'J. Halpert', probability: 50,
      source: 'Cold Call', region: 'KZN', temperature: 'Warm', nextAction: 'Send Revised Quote', nextActionDate: '2024-05-22',
      activities: [], productMix: { equipment: 0, service: 120000, spares: 0 }, gpMargin: 35
  },
  { 
      id: 'LD-108', company: 'Sappi Saiccor', contactPerson: 'David Wallace', email: 'd.wallace@sappi.com', phone: '011 999 8888',
      value: 850000, stage: 'Qualified', owner: 'D. Schrute', probability: 20,
      source: 'Website', region: 'JHB', temperature: 'Cold', nextAction: 'Schedule Site Visit', nextActionDate: '2024-06-01',
      activities: [], productMix: { equipment: 800000, service: 50000, spares: 0 }, gpMargin: 22
  },
  { 
      id: 'LD-112', company: 'Toyota', contactPerson: 'Jim Halpert', email: 'j.halpert@toyota.com', phone: '031 910 2020',
      value: 65000, stage: 'Lead', owner: 'M. Scott', probability: 10,
      source: 'Rental Enquiry', region: 'DBN', temperature: 'Warm', nextAction: 'Initial Call', nextActionDate: '2024-05-20',
      activities: [], productMix: { equipment: 0, service: 65000, spares: 0 }
  },
];

const initialTargets: SalesTarget[] = [
    { id: 'TGT-001', repId: 'Global', repName: 'Company Wide', period: 'May 2024', visitTarget: 50, callTarget: 200, revenueTarget: 1500000, dealsClosedTarget: 10, actualVisits: 32, actualCalls: 145, actualRevenue: 950000, actualDeals: 6 },
    { id: 'TGT-002', repId: 'REP-01', repName: 'M. Scott', period: 'May 2024', visitTarget: 15, callTarget: 60, revenueTarget: 500000, dealsClosedTarget: 3, actualVisits: 12, actualCalls: 55, actualRevenue: 450000, actualDeals: 2 },
    { id: 'TGT-003', repId: 'REP-02', repName: 'D. Schrute', period: 'May 2024', visitTarget: 20, callTarget: 80, revenueTarget: 600000, dealsClosedTarget: 4, actualVisits: 18, actualCalls: 70, actualRevenue: 300000, actualDeals: 1 },
];

const initialExpenses: ExpenseTransaction[] = [
  { 
    id: 'EXP-001', date: '2024-05-15', merchant: 'Shell Ultra City', amount: 1250.00, currency: 'ZAR', 
    paymentMethod: 'Company Card', category: 'Fuel', description: 'Diesel for vehicle reg ND 123-456', 
    jobId: 'JOB-2024-001', costCentre: 'Service', isBillable: true,
    status: 'Manager Approved', submittedBy: 'J. Smith', approvedBy: 'S. Nkosi', approvalDate: '2024-05-16',
    receiptUrl: 'receipt_shell.jpg', isCardFeed: true, vatAmount: 163.04
  },
  { 
    id: 'EXP-002', date: '2024-05-16', merchant: 'Bearing Man', amount: 4500.00, currency: 'ZAR', 
    paymentMethod: 'Company Card', category: 'Parts/Spares', description: 'Emergency seal kit for compressor', 
    jobId: 'JOB-2024-001', costCentre: 'Service', isBillable: true,
    status: 'Submitted', submittedBy: 'J. Smith',
    receiptUrl: 'receipt_bmg.pdf', isCardFeed: true, vatAmount: 586.96
  },
  { 
    id: 'EXP-003', date: '2024-05-18', merchant: 'City Lodge', amount: 1800.00, currency: 'ZAR', 
    paymentMethod: 'Cash (Pocket)', category: 'Accommodation', description: 'Overnight stay for Rustenburg job', 
    jobId: 'JOB-2024-001', costCentre: 'Service', isBillable: true,
    status: 'Draft', submittedBy: 'J. Smith', isCardFeed: false
  },
  { 
    id: 'EXP-004', date: '2024-05-20', merchant: 'Mugg & Bean', amount: 350.00, currency: 'ZAR', 
    paymentMethod: 'Company Card', category: 'Meals', description: 'Lunch with client', 
    customerId: 'CUST-001', costCentre: 'Sales', isBillable: false,
    status: 'Submitted', submittedBy: 'M. Scott', isCardFeed: true, vatAmount: 45.65
  },
];

const initialContractors: Contractor[] = [
    {
        id: 'CON-001', name: 'Spark Electrical', tradingName: 'Spark Electrical KZN', regNumber: '2010/001234/07', status: 'Active',
        grade: 'A', riskLevel: 'Low', prequalScore: 92, trades: ['Electrical', 'Instrumentation'], contactPerson: 'Joe Spark', email: 'joe@spark.co.za', phone: '083 555 1234',
        documents: [
            { id: 'DOC-1', type: 'COIDA', expiryDate: '2025-02-28', status: 'Valid' },
            { id: 'DOC-2', type: 'Public Liability', expiryDate: '2024-12-31', status: 'Valid' }
        ],
        employees: [
            { id: 'EMP-1', name: 'Sam Jones', role: 'Electrician', competencies: ['Wiremans License', 'Lock Out Tag Out'] }
        ],
        performanceHistory: [
            { jobId: 'JOB-2024-001', date: '2024-04-15', hsseScore: 100, qualityScore: 95, scheduleScore: 90, averageScore: 95, rater: 'S. Nkosi' }
        ]
    },
    {
        id: 'CON-002', name: 'Rigging Pros', tradingName: 'Rigging Pros SA', regNumber: '2015/998877/07', status: 'Active',
        grade: 'B', riskLevel: 'Medium', prequalScore: 85, trades: ['Rigging', 'Lifting'], contactPerson: 'Mike Lift', email: 'mike@riggingpros.co.za', phone: '082 444 9988',
        documents: [
            { id: 'DOC-3', type: 'COIDA', expiryDate: '2024-06-01', status: 'Expiring Soon' },
            { id: 'DOC-4', type: 'Public Liability', expiryDate: '2025-01-01', status: 'Valid' }
        ],
        employees: [],
        performanceHistory: []
    },
    {
        id: 'CON-003', name: 'CleanCo', tradingName: 'CleanCo Industrial', regNumber: '2018/112233/07', status: 'Suspended',
        grade: 'C', riskLevel: 'High', prequalScore: 65, trades: ['Cleaning', 'Waste'], contactPerson: 'Sarah Clean', email: 'sarah@cleanco.co.za', phone: '011 222 3344',
        documents: [
            { id: 'DOC-5', type: 'COIDA', expiryDate: '2023-12-31', status: 'Expired' }
        ],
        employees: [],
        performanceHistory: [
            { jobId: 'JOB-2024-005', date: '2024-03-10', hsseScore: 40, qualityScore: 60, scheduleScore: 50, averageScore: 50, rater: 'J. Smith' }
        ]
    }
];

const initialEmployees: Employee[] = [
    {
        id: 'EMP-001', firstName: 'John', lastName: 'Smith', email: 'j.smith@titan.com', phone: '082 111 2222', position: 'Snr Technician', department: 'Operations', startDate: '2020-05-01', status: 'Active', leaveBalance: 15,
        idNumber: '8801205555081', dob: '1988-01-20', gender: 'Male', nationality: 'South African', address: '12 Main Rd, Westville, Durban', employmentType: 'Permanent', managerName: 'S. Nkosi',
        ppeIssued: [
            { id: 'PPE-1', item: 'Safety Boots', issueDate: '2023-01-15', expiryDate: '2024-01-15', status: 'Expired' },
            { id: 'PPE-2', item: 'Hard Hat', issueDate: '2023-06-01', expiryDate: '2025-06-01', status: 'Valid' }
        ],
        competencies: [
            { id: 'COMP-1', name: 'Working at Heights', issueDate: '2023-03-01', expiryDate: '2025-03-01', status: 'Valid', requiredForRole: true, documents: [] },
            { id: 'COMP-2', name: 'Basic Fire Fighting', issueDate: '2022-01-01', expiryDate: '2024-01-01', status: 'Expired', requiredForRole: true, documents: [{ id: 'DOC-99', name: 'FireCert.pdf', url: '#', uploadDate: '2022-01-05', type: 'Certificate' }] }
        ],
        performanceHistory: [
            { 
                id: 'PERF-1', period: 'FY2023', rating: 4.2, date: '2023-12-15', managerComments: 'Excellent technical work.', reviewer: 'D. Manager', 
                documents: [{ id: 'REV-001', name: 'Signed_FY23_Review.pdf', url: '#', uploadDate: '2023-12-20', type: 'Review' }],
                scorecard: [
                    { id: 'SC-1', category: 'Operational', criteria: 'First Time Fix Rate', methodology: 'Percentage of jobs closed without rework within 7 days', weight: 40, target: '95%', actual: '92%', score: 4, evidence: [] },
                    { id: 'SC-2', category: 'Safety', criteria: 'Safety Incidents', methodology: 'Number of recorded safety incidents or near misses involving the employee', weight: 30, target: '0', actual: '0', score: 5, evidence: [] }
                ],
                pdp: [
                    { id: 'PDP-1', objective: 'Lead Large Scale Install', actionPlan: 'Shadow Senior Project Manager on 3 major installs, then lead one under supervision.', targetDate: '2023-11-30', status: 'Completed', evidence: [] }
                ]
            }
        ],
        driversLicense: { code: 'C1', number: 'ZN-5521922', issueDate: '2020-05-15', expiryDate: '2025-05-15', prdpExpiry: '2024-05-15' },
        medical: { status: 'Fit', examDate: '2023-06-01', expiryDate: '2024-06-01', allergies: 'Penicillin' },
        clothingSizes: { shirt: 'L', trousers: '34', overalls: 'L', safetyShoes: '9', jacket: 'L', gloves: 'L' },
        nextOfKin: [{ id: 'NOK-1', name: 'Jane Smith', relationship: 'Spouse', phone: '082 999 8888', address: 'Same as employee' }]
    },
    {
        id: 'EMP-002', firstName: 'Sarah', lastName: 'Nkosi', email: 's.nkosi@titan.com', phone: '083 333 4444', position: 'HSE Officer', department: 'HSSE', startDate: '2021-08-10', status: 'Active', leaveBalance: 20,
        idNumber: '9205150000182', dob: '1992-05-15', gender: 'Female', nationality: 'South African', address: '45 Beach Rd, Durban', employmentType: 'Permanent', managerName: 'D. Director',
        ppeIssued: [],
        competencies: [
            { id: 'COMP-3', name: 'SAMTRAC', issueDate: '2021-01-01', expiryDate: '2026-01-01', status: 'Valid', requiredForRole: true, documents: [] }
        ],
        performanceHistory: [],
        clothingSizes: { shirt: 'M', trousers: '32', overalls: 'M', safetyShoes: '6', jacket: 'M', gloves: 'M' }
    }
];

const initialLeave: LeaveRequest[] = [
    { id: 'LV-101', employeeId: 'EMP-001', type: 'Annual', startDate: '2024-06-10', endDate: '2024-06-14', days: 5, status: 'Pending', reason: 'Family holiday' },
    { id: 'LV-102', employeeId: 'EMP-002', type: 'Sick', startDate: '2024-05-01', endDate: '2024-05-02', days: 2, status: 'Approved', approver: 'D. Manager' }
];

const initialOvertime: OvertimeEntry[] = [
    { id: 'OT-550', employeeId: 'EMP-001', date: '2024-05-18', hours: 4, jobId: 'JOB-2024-001', reason: 'Emergency breakdown repair', status: 'Approved', approver: 'S. Nkosi' }
];

const budgets: Budget[] = [
  { department: 'Service', monthlyLimit: 50000, currentSpend: 32450, year: 2024, month: 5 },
  { department: 'Sales', monthlyLimit: 20000, currentSpend: 12800, year: 2024, month: 5 },
];

const categoryData = [
  { name: 'Fuel', value: 12500 },
  { name: 'Parts', value: 45000 },
  { name: 'Travel', value: 8500 },
  { name: 'Meals', value: 3200 },
];
const COLORS = ['#cd8832', '#3b82f6', '#10b981', '#ef4444'];

// --- COMPONENTS ---

const StatusBadge = ({ status }: { status: string }) => {
    const colors: Record<string, string> = {
        'Draft': 'bg-slate-700 text-slate-300',
        'Submitted': 'bg-blue-900/30 text-blue-400',
        'Manager Approved': 'bg-orange-900/30 text-orange-400',
        'Finance Approved': 'bg-green-900/30 text-green-400',
        'Paid': 'bg-green-600 text-white',
        'Rejected': 'bg-red-900/30 text-red-400',
        'Active': 'bg-green-500/20 text-green-400',
        'Probation': 'bg-yellow-500/20 text-yellow-400',
        'Suspended': 'bg-red-500/20 text-red-400',
        'Blacklisted': 'bg-slate-800 text-slate-500 border border-slate-600',
        'Pending': 'bg-blue-500/20 text-blue-400',
        'Valid': 'bg-green-500/20 text-green-400',
        'Expiring Soon': 'bg-orange-500/20 text-orange-400',
        'Expired': 'bg-red-500/20 text-red-400',
        'Missing': 'bg-slate-700 text-slate-400',
        'Approved': 'bg-green-500/20 text-green-400',
        'Cancelled': 'bg-slate-700 text-slate-400',
        'Expiring': 'bg-orange-500/20 text-orange-400',
        'Completed': 'bg-green-600/20 text-green-400',
        'Not Started': 'bg-slate-600 text-slate-300',
        'In Progress': 'bg-blue-500/20 text-blue-400',
        'Fit': 'bg-green-500/20 text-green-400',
        'Unfit': 'bg-red-500/20 text-red-400',
        'Fit with Restrictions': 'bg-orange-500/20 text-orange-400'
    };
    return <span className={`px-2 py-1 rounded text-xs font-bold ${colors[status] || 'bg-slate-600'}`}>{status}</span>;
};

const ProgressBar = ({ current, target, color = 'bg-titan-500' }: { current: number, target: number, color?: string }) => {
    const percentage = Math.min(100, Math.max(0, (current / target) * 100));
    return (
        <div className="w-full bg-slate-700 rounded-full h-2.5 mb-1">
            <div className={`h-2.5 rounded-full ${color}`} style={{ width: `${percentage}%` }}></div>
        </div>
    );
};

const Modal = ({ title, onClose, children, size='md' }: { title: string, onClose: () => void, children: React.ReactNode, size?: 'md'|'lg'|'xl' }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fade-in">
        <div className={`bg-slate-800 rounded-lg border border-slate-700 w-full shadow-2xl flex flex-col max-h-[90vh] ${size === 'xl' ? 'max-w-6xl' : size === 'lg' ? 'max-w-4xl' : 'max-w-2xl'}`}>
            <div className="p-4 border-b border-slate-700 flex justify-between items-center bg-slate-900 rounded-t-lg">
                <h3 className="text-xl font-bold text-white">{title}</h3>
                <button onClick={onClose} className="text-slate-400 hover:text-white"><XCircle className="w-5 h-5"/></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
                {children}
            </div>
        </div>
    </div>
);

const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement> & { label: string }> = ({ label, className = "", ...props }) => (
    <div>
        <label className="block text-xs text-slate-400 uppercase font-bold mb-1">{label}</label>
        <input 
            className={`w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none focus:ring-1 focus:ring-titan-500 [&::-webkit-calendar-picker-indicator]:invert ${className}`} 
            {...props}
        />
    </div>
);

const AddContractorDocModal = ({ onClose, onSave }: { onClose: () => void, onSave: (doc: ContractorDocument) => void }) => {
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const formData = new FormData(e.target as HTMLFormElement);
        const expiry = formData.get('expiry') as string;
        
        // Determine status based on expiry
        let status: 'Valid' | 'Expiring Soon' | 'Expired' = 'Valid';
        const expiryDate = new Date(expiry);
        const today = new Date();
        const thirtyDays = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);

        if (expiryDate < today) status = 'Expired';
        else if (expiryDate < thirtyDays) status = 'Expiring Soon';

        const newDoc: ContractorDocument = {
            id: `DOC-${Date.now()}`,
            type: formData.get('type') as DocType,
            expiryDate: expiry,
            status: status,
            fileUrl: 'uploaded_file.pdf',
            verifiedBy: 'Current User'
        };
        onSave(newDoc);
    }

    return (
        <Modal title="Upload Compliance Document" onClose={onClose}>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Document Type</label>
                    <select name="type" className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none">
                        <option value="COIDA">COIDA / Good Standing</option>
                        <option value="Public Liability">Public Liability Insurance</option>
                        <option value="HSE Plan">HSE Plan</option>
                        <option value="Tax Clearance">Tax Clearance</option>
                        <option value="B-BBEE">B-BBEE Certificate</option>
                        <option value="ISO 9001">ISO 9001</option>
                        <option value="Other">Other</option>
                    </select>
                </div>
                <div>
                    <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Expiry Date</label>
                    <input name="expiry" type="date" required className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none" />
                </div>
                <div className="border-2 border-dashed border-slate-600 rounded p-6 flex flex-col items-center justify-center text-slate-500 hover:bg-slate-900/50 cursor-pointer transition">
                    <Upload className="w-8 h-8 mb-2 opacity-50" />
                    <span className="text-sm">Click to Upload File (PDF/JPG)</span>
                </div>
                <div className="flex justify-end pt-2">
                    <button type="submit" className="bg-titan-500 hover:bg-titan-600 text-white px-4 py-2 rounded font-bold">Save Document</button>
                </div>
            </form>
        </Modal>
    );
};

const AddContractorEmployeeModal = ({ onClose, onSave }: { onClose: () => void, onSave: (emp: ContractorEmployee) => void }) => {
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const formData = new FormData(e.target as HTMLFormElement);
        const newEmp: ContractorEmployee = {
            id: `EMP-${Date.now()}`,
            name: formData.get('name') as string,
            role: formData.get('role') as string,
            competencies: (formData.get('competencies') as string).split(',').map(s => s.trim()).filter(s => s !== '')
        };
        onSave(newEmp);
    }

    return (
        <Modal title="Add Contractor Employee" onClose={onClose}>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Full Name</label>
                    <input name="name" required className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none" />
                </div>
                <div>
                    <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Role / Job Title</label>
                    <input name="role" required className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none" />
                </div>
                <div>
                    <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Competencies / Qualifications</label>
                    <textarea name="competencies" placeholder="e.g. Electrician, Working at Heights, Confined Space (Comma separated)" className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none h-24"></textarea>
                </div>
                <div className="flex justify-end pt-2">
                    <button type="submit" className="bg-titan-500 hover:bg-titan-600 text-white px-4 py-2 rounded font-bold">Add Employee</button>
                </div>
            </form>
        </Modal>
    );
};

// --- VIEWS ---

export const SalesCRMView: React.FC = () => {
  const [view, setView] = useState<'Dashboard' | 'Pipeline' | 'Leads' | 'Activities' | 'Targets'>('Dashboard');
  const [leads, setLeads] = useState<SalesLead[]>(initialLeads);
  const [targets, setTargets] = useState<SalesTarget[]>(initialTargets);
  const [showModal, setShowModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState<SalesLead | null>(null);
  const [selectedTarget, setSelectedTarget] = useState<SalesTarget | null>(null);

  const STAGES: LeadStage[] = ['Lead', 'Qualified', 'Site Visit', 'Quote', 'Negotiation', 'Won', 'Lost'];

  // Flattened activities list for the Activities View
  const allActivities = leads.flatMap(l => l.activities.map(a => ({...a, leadName: l.company, leadId: l.id})))
                             .sort((a,b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  // Handlers
  const handleMoveStage = (leadId: string, direction: 'next' | 'prev') => {
      const lead = leads.find(l => l.id === leadId);
      if(!lead) return;
      
      const currIdx = STAGES.indexOf(lead.stage);
      let newIdx = direction === 'next' ? currIdx + 1 : currIdx - 1;
      
      if(newIdx >= 0 && newIdx < STAGES.length) {
          const newStage = STAGES[newIdx];
          const newProb = newStage === 'Won' ? 100 : newStage === 'Lost' ? 0 : (newIdx * 15) + 10;
          
          setLeads(leads.map(l => l.id === leadId ? { ...l, stage: newStage, probability: newProb } : l));
      }
  };

  const handleSaveLead = (e: React.FormEvent) => {
      e.preventDefault();
      const formData = new FormData(e.target as HTMLFormElement);
      
      const newLead: SalesLead = {
          id: selectedLead?.id || `LD-${Date.now().toString().slice(-4)}`,
          company: formData.get('company') as string,
          contactPerson: formData.get('contact') as string,
          email: formData.get('email') as string,
          phone: formData.get('phone') as string,
          value: Number(formData.get('value')),
          stage: (formData.get('stage') as LeadStage) || 'Lead',
          owner: 'Current User',
          probability: Number(formData.get('probability')) || 10,
          source: formData.get('source') as string,
          region: formData.get('region') as string,
          temperature: formData.get('temperature') as any,
          nextAction: formData.get('nextAction') as string,
          nextActionDate: formData.get('nextActionDate') as string,
          gpMargin: Number(formData.get('gpMargin')),
          activities: selectedLead?.activities || [],
          productMix: {
              equipment: Number(formData.get('mixEquip')),
              service: Number(formData.get('mixService')),
              spares: 0
          }
      };

      if(selectedLead) {
          setLeads(leads.map(l => l.id === newLead.id ? newLead : l));
      } else {
          setLeads([...leads, newLead]);
      }
      setShowModal(false);
      setSelectedLead(null);
  };

  const handleSaveActivity = (e: React.FormEvent) => {
      e.preventDefault();
      const formData = new FormData(e.target as HTMLFormElement);
      const leadId = formData.get('leadId') as string;
      const lead = leads.find(l => l.id === leadId);
      
      if (!lead) return;

      const newActivity: SalesActivity = {
          id: `ACT-${Date.now()}`,
          type: formData.get('type') as any,
          date: formData.get('date') as string,
          notes: formData.get('notes') as string,
          outcome: formData.get('outcome') as string,
          loggedBy: 'Current User'
      };

      const updatedLead = { ...lead, activities: [newActivity, ...lead.activities] };
      setLeads(leads.map(l => l.id === leadId ? updatedLead : l));
      setShowActivityModal(false);
  };

  const handleSaveTarget = (e: React.FormEvent) => {
      e.preventDefault();
      const formData = new FormData(e.target as HTMLFormElement);
      if(!selectedTarget) return;

      const updatedTarget: SalesTarget = {
          ...selectedTarget,
          callTarget: Number(formData.get('callTarget')),
          visitTarget: Number(formData.get('visitTarget')),
          revenueTarget: Number(formData.get('revenueTarget')),
          dealsClosedTarget: Number(formData.get('dealsTarget'))
      };

      setTargets(targets.map(t => t.id === updatedTarget.id ? updatedTarget : t));
      setShowTargetModal(false);
      setSelectedTarget(null);
  }

  const renderDashboard = () => (
      <div className="space-y-6 animate-fade-in">
          {/* Top KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-slate-800 p-4 rounded border-t-4 border-titan-500">
                  <div className="text-slate-400 text-xs uppercase font-bold">New Equipment Sales</div>
                  <div className="text-2xl font-bold text-white">R {leads.filter(l => l.stage === 'Won').reduce((acc, curr) => acc + (curr.productMix?.equipment || 0), 0).toLocaleString()}</div>
                  <div className="text-green-500 text-xs">Target: R 400k / month</div>
              </div>
              <div className="bg-slate-800 p-4 rounded border-t-4 border-blue-500">
                  <div className="text-slate-400 text-xs uppercase font-bold">Pipeline Value</div>
                  <div className="text-2xl font-bold text-white">R {leads.filter(l => l.stage !== 'Won' && l.stage !== 'Lost').reduce((acc, curr) => acc + curr.value, 0).toLocaleString()}</div>
                  <div className="text-slate-500 text-xs">Coverage: 3.2x Target</div>
              </div>
              <div className="bg-slate-800 p-4 rounded border-t-4 border-green-500">
                  <div className="text-slate-400 text-xs uppercase font-bold">Win Rate</div>
                  <div className="text-2xl font-bold text-white">28%</div>
                  <div className="text-slate-500 text-xs">Industry Avg: 25%</div>
              </div>
              <div className="bg-slate-800 p-4 rounded border-t-4 border-purple-500">
                  <div className="text-slate-400 text-xs uppercase font-bold">Service Revenue Driven</div>
                  <div className="text-2xl font-bold text-white">R {leads.filter(l => l.stage === 'Won').reduce((acc, curr) => acc + (curr.productMix?.service || 0), 0).toLocaleString()}</div>
                  <div className="text-green-500 text-xs">Target: R 250k / month</div>
              </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-slate-800 rounded-lg border border-slate-700 p-6">
                  <h3 className="text-lg font-bold text-white mb-4">Pipeline by Stage</h3>
                  <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={STAGES.map(s => ({ 
                              name: s, 
                              value: leads.filter(l => l.stage === s).reduce((acc, curr) => acc + curr.value, 0) 
                          }))}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                              <XAxis dataKey="name" stroke="#94a3b8" />
                              <YAxis stroke="#94a3b8" />
                              <Tooltip contentStyle={{backgroundColor: '#1e293b', borderColor: '#334155'}} />
                              <Bar dataKey="value" fill="#cd8832" />
                          </BarChart>
                      </ResponsiveContainer>
                  </div>
              </div>
              <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
                  <h3 className="text-lg font-bold text-white mb-4">Sales Activity</h3>
                  <div className="space-y-4">
                      <div className="flex justify-between items-center border-b border-slate-700 pb-2">
                          <span className="text-slate-400">Site Visits</span>
                          <span className="text-white font-bold">12</span>
                      </div>
                      <div className="flex justify-between items-center border-b border-slate-700 pb-2">
                          <span className="text-slate-400">Quotes Sent</span>
                          <span className="text-white font-bold">8</span>
                      </div>
                      <div className="flex justify-between items-center border-b border-slate-700 pb-2">
                          <span className="text-slate-400">Calls Logged</span>
                          <span className="text-white font-bold">45</span>
                      </div>
                      <div className="pt-2 text-center">
                          <div className="text-xs text-titan-500 font-bold uppercase">Conversion Rate</div>
                          <div className="text-2xl text-white font-bold">18%</div>
                          <div className="text-xs text-slate-500">Visit to Quote</div>
                      </div>
                  </div>
              </div>
          </div>
      </div>
  );

  const renderPipeline = () => (
      <div className="flex gap-4 overflow-x-auto pb-4 h-[calc(100vh-200px)]">
          {STAGES.map(stage => (
              <div key={stage} className="min-w-[300px] bg-slate-900 rounded-lg border border-slate-800 flex flex-col">
                  <div className={`p-3 border-b-4 ${stage === 'Won' ? 'border-green-500' : stage === 'Lost' ? 'border-red-500' : 'border-titan-500'} bg-slate-800 rounded-t-lg flex justify-between items-center sticky top-0 z-10`}>
                      <span className="font-bold text-white uppercase text-sm">{stage}</span>
                      <span className="bg-slate-900 text-slate-400 text-xs px-2 py-1 rounded-full">
                          {leads.filter(l => l.stage === stage).length}
                      </span>
                  </div>
                  <div className="p-2 space-y-2 flex-1 overflow-y-auto">
                      {leads.filter(l => l.stage === stage).map(lead => (
                          <div key={lead.id} className="bg-slate-800 p-3 rounded border border-slate-700 hover:border-titan-500 transition shadow-sm group">
                              <div className="flex justify-between items-start mb-2">
                                  <h4 className="text-white font-bold text-sm truncate w-40">{lead.company}</h4>
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-bold ${lead.temperature === 'Hot' ? 'bg-red-500 text-white' : lead.temperature === 'Warm' ? 'bg-orange-500 text-white' : 'bg-blue-500 text-white'}`}>
                                      {lead.temperature}
                                  </span>
                              </div>
                              <div className="text-titan-400 font-bold text-sm mb-1">R {lead.value.toLocaleString()}</div>
                              <div className="text-xs text-slate-400 mb-2">
                                  <User className="w-3 h-3 inline mr-1"/> {lead.contactPerson}
                              </div>
                              
                              {lead.nextAction && (
                                  <div className={`text-xs p-2 rounded mb-3 flex items-center gap-2 ${new Date(lead.nextActionDate!) < new Date() ? 'bg-red-900/20 text-red-400' : 'bg-slate-900 text-slate-300'}`}>
                                      <Clock className="w-3 h-3" />
                                      <div className="truncate">
                                          {lead.nextAction} 
                                          <span className="block text-[10px] opacity-70">{lead.nextActionDate}</span>
                                      </div>
                                  </div>
                              )}

                              <div className="flex justify-between border-t border-slate-700 pt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button onClick={() => handleMoveStage(lead.id, 'prev')} className="text-slate-500 hover:text-white" disabled={stage==='Lead'}><ArrowRight className="w-4 h-4 rotate-180" /></button>
                                  <button onClick={() => { setSelectedLead(lead); setShowModal(true); }} className="text-titan-500 hover:text-white text-xs font-bold uppercase">View</button>
                                  <button onClick={() => handleMoveStage(lead.id, 'next')} className="text-slate-500 hover:text-white" disabled={stage==='Lost'}><ArrowRight className="w-4 h-4" /></button>
                              </div>
                          </div>
                      ))}
                  </div>
                  <div className="p-3 bg-slate-950 rounded-b-lg border-t border-slate-800 text-center">
                      <div className="text-xs text-slate-500 uppercase">Total Value</div>
                      <div className="text-sm font-bold text-slate-300">R {leads.filter(l => l.stage === stage).reduce((acc, curr) => acc + curr.value, 0).toLocaleString()}</div>
                  </div>
              </div>
          ))}
      </div>
  );

  const renderActivities = () => (
      <div className="space-y-6 animate-fade-in">
          <div className="flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">Activity Log</h3>
              <button onClick={() => setShowActivityModal(true)} className="bg-titan-500 hover:bg-titan-600 text-white px-4 py-2 rounded font-bold flex items-center gap-2">
                  <Phone className="w-4 h-4" /> Log Activity
              </button>
          </div>

          <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
              <table className="w-full text-left">
                  <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
                      <tr>
                          <th className="px-6 py-4">Date</th>
                          <th className="px-6 py-4">Type</th>
                          <th className="px-6 py-4">Lead / Company</th>
                          <th className="px-6 py-4">Outcome</th>
                          <th className="px-6 py-4">Logged By</th>
                          <th className="px-6 py-4">Notes</th>
                      </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                      {allActivities.length > 0 ? allActivities.map(act => (
                          <tr key={act.id} className="hover:bg-slate-750">
                              <td className="px-6 py-4 text-slate-300 font-mono text-xs">{act.date}</td>
                              <td className="px-6 py-4">
                                  <span className={`px-2 py-1 rounded text-xs font-bold flex items-center gap-2 w-fit ${
                                      act.type === 'Call' ? 'bg-blue-900/30 text-blue-400' :
                                      act.type === 'Site Visit' ? 'bg-green-900/30 text-green-400' :
                                      act.type === 'Email' ? 'bg-slate-700 text-slate-300' : 'bg-purple-900/30 text-purple-400'
                                  }`}>
                                      {act.type === 'Call' && <Phone className="w-3 h-3"/>}
                                      {act.type === 'Site Visit' && <MapPin className="w-3 h-3"/>}
                                      {act.type === 'Email' && <Mail className="w-3 h-3"/>}
                                      {act.type}
                                  </span>
                              </td>
                              <td className="px-6 py-4 text-white font-medium">{act.leadName}</td>
                              <td className="px-6 py-4 text-slate-300">{act.outcome || '-'}</td>
                              <td className="px-6 py-4 text-slate-400 text-sm">{act.loggedBy}</td>
                              <td className="px-6 py-4 text-slate-500 text-sm truncate max-w-xs">{act.notes}</td>
                          </tr>
                      )) : (
                          <tr><td colSpan={6} className="p-8 text-center text-slate-500 italic">No activities logged yet.</td></tr>
                      )}
                  </tbody>
              </table>
          </div>
      </div>
  );

  const renderTargets = () => (
      <div className="space-y-6 animate-fade-in">
          <div className="flex justify-between items-center mb-6">
              <div>
                  <h3 className="text-xl font-bold text-white">Performance Targets</h3>
                  <p className="text-slate-400 text-sm">Period: May 2024</p>
              </div>
          </div>

          <div className="grid grid-cols-1 gap-6">
              {targets.map(target => (
                  <div key={target.id} className="bg-slate-800 p-6 rounded-lg border border-slate-700">
                      <div className="flex justify-between items-start mb-6 border-b border-slate-700 pb-4">
                          <div className="flex items-center gap-3">
                              <div className={`p-3 rounded-full ${target.repId === 'Global' ? 'bg-titan-500/20 text-titan-500' : 'bg-blue-900/20 text-blue-400'}`}>
                                  {target.repId === 'Global' ? <Building2 className="w-6 h-6" /> : <User className="w-6 h-6" />}
                              </div>
                              <div>
                                  <h4 className="text-lg font-bold text-white">{target.repName}</h4>
                                  <span className="text-xs text-slate-500 uppercase">{target.repId === 'Global' ? 'Company Wide' : 'Sales Representative'}</span>
                              </div>
                          </div>
                          <button onClick={() => { setSelectedTarget(target); setShowTargetModal(true); }} className="text-xs bg-slate-700 hover:bg-slate-600 text-white px-3 py-1 rounded">Edit Targets</button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                          <div>
                              <div className="flex justify-between text-sm mb-1">
                                  <span className="text-slate-400 flex items-center gap-1"><Phone className="w-3 h-3"/> Calls</span>
                                  <span className="text-white font-bold">{target.actualCalls} / {target.callTarget}</span>
                              </div>
                              <ProgressBar current={target.actualCalls || 0} target={target.callTarget} color="bg-blue-500" />
                          </div>
                          <div>
                              <div className="flex justify-between text-sm mb-1">
                                  <span className="text-slate-400 flex items-center gap-1"><MapPin className="w-3 h-3"/> Site Visits</span>
                                  <span className="text-white font-bold">{target.actualVisits} / {target.visitTarget}</span>
                              </div>
                              <ProgressBar current={target.actualVisits || 0} target={target.visitTarget} color="bg-green-500" />
                          </div>
                          <div>
                              <div className="flex justify-between text-sm mb-1">
                                  <span className="text-slate-400 flex items-center gap-1"><Target className="w-3 h-3"/> Deals Closed</span>
                                  <span className="text-white font-bold">{target.actualDeals} / {target.dealsClosedTarget}</span>
                              </div>
                              <ProgressBar current={target.actualDeals || 0} target={target.dealsClosedTarget} color="bg-purple-500" />
                          </div>
                          <div>
                              <div className="flex justify-between text-sm mb-1">
                                  <span className="text-slate-400 flex items-center gap-1"><DollarSign className="w-3 h-3"/> Revenue</span>
                                  <span className="text-white font-bold">{(target.actualRevenue || 0) / 1000}k / {target.revenueTarget / 1000}k</span>
                              </div>
                              <ProgressBar current={target.actualRevenue || 0} target={target.revenueTarget} color="bg-titan-500" />
                          </div>
                      </div>
                  </div>
              ))}
          </div>
      </div>
  );

  return (
    <div className="h-full flex flex-col">
        {/* Header Controls */}
        <div className="flex justify-between items-center flex-none mb-6 min-h-[4rem]">
             <div className="flex gap-4 bg-slate-900 p-1 rounded-lg border border-slate-800">
                 {['Dashboard', 'Pipeline', 'Leads', 'Activities', 'Targets'].map(t => (
                     <button 
                        key={t}
                        onClick={() => setView(t as any)}
                        className={`px-4 py-2 rounded-md text-sm font-bold transition ${view === t ? 'bg-slate-700 text-white shadow' : 'text-slate-400 hover:text-white'}`}
                     >
                         {t}
                     </button>
                 ))}
             </div>
             <button onClick={() => { setSelectedLead(null); setShowModal(true); }} className="bg-titan-500 hover:bg-titan-600 text-white px-4 py-2 rounded font-bold flex items-center gap-2 shadow-lg">
                 <Plus className="w-4 h-4" /> Add Lead
             </button>
        </div>

        {/* Views */}
        <div className="flex-1 overflow-y-auto">
            {view === 'Dashboard' && renderDashboard()}
            {view === 'Pipeline' && renderPipeline()}
            {view === 'Activities' && renderActivities()}
            {view === 'Targets' && renderTargets()}
            
            {view === 'Leads' && (
                <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
                    <table className="w-full text-left">
                        <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
                            <tr>
                                <th className="px-6 py-4">Lead</th>
                                <th className="px-6 py-4">Source</th>
                                <th className="px-6 py-4">Region</th>
                                <th className="px-6 py-4">Status</th>
                                <th className="px-6 py-4">Next Action</th>
                                <th className="px-6 py-4">Est. Value</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700">
                            {leads.map(lead => (
                                <tr key={lead.id} onClick={() => { setSelectedLead(lead); setShowModal(true); }} className="hover:bg-slate-750 cursor-pointer">
                                    <td className="px-6 py-4">
                                        <div className="text-white font-bold">{lead.company}</div>
                                        <div className="text-xs text-slate-500">{lead.contactPerson}</div>
                                    </td>
                                    <td className="px-6 py-4 text-slate-300">{lead.source}</td>
                                    <td className="px-6 py-4 text-slate-300">{lead.region}</td>
                                    <td className="px-6 py-4"><StatusBadge status={lead.stage} /></td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm text-white">{lead.nextAction || '-'}</div>
                                        <div className="text-xs text-slate-500">{lead.nextActionDate}</div>
                                    </td>
                                    <td className="px-6 py-4 text-white font-mono">R {lead.value.toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>

        {/* Add/Edit Modal */}
        {showModal && (
            <Modal title={selectedLead ? "Manage Opportunity" : "Capture New Lead"} onClose={() => setShowModal(false)} size="xl">
                <form onSubmit={handleSaveLead} className="space-y-6">
                    {/* Basic Info */}
                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <h4 className="text-titan-500 font-bold text-sm uppercase mb-3 border-b border-slate-700 pb-1">Company Details</h4>
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Company Name</label>
                                        <input name="company" defaultValue={selectedLead?.company} required className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none" />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Region</label>
                                        <select name="region" defaultValue={selectedLead?.region} className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none">
                                            <option>JHB</option><option>DBN</option><option>CPT</option><option>Exports</option>
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Source</label>
                                    <select name="source" defaultValue={selectedLead?.source} className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none">
                                        <option>Website</option><option>Cold Call</option><option>Referral</option><option>Rental Enquiry</option><option>Field Visit</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div>
                            <h4 className="text-titan-500 font-bold text-sm uppercase mb-3 border-b border-slate-700 pb-1">Contact Person</h4>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Name</label>
                                    <input name="contact" defaultValue={selectedLead?.contactPerson} required className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Email</label>
                                        <input name="email" type="email" defaultValue={selectedLead?.email} className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none" />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Phone</label>
                                        <input name="phone" defaultValue={selectedLead?.phone} className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Deal Info */}
                    <div>
                        <h4 className="text-titan-500 font-bold text-sm uppercase mb-3 border-b border-slate-700 pb-1">Opportunity Details</h4>
                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Total Value (Est.)</label>
                                <input name="value" type="number" defaultValue={selectedLead?.value} className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none" />
                            </div>
                            <div>
                                <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Stage</label>
                                <select name="stage" defaultValue={selectedLead?.stage} className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none">
                                    {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Probability (%)</label>
                                <input name="probability" type="number" defaultValue={selectedLead?.probability} className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none" />
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-4 mt-4 bg-slate-900 p-4 rounded border border-slate-700">
                            <div>
                                <label className="block text-xs text-slate-500 uppercase font-bold mb-1">Equipment Split</label>
                                <input name="mixEquip" type="number" placeholder="Value" defaultValue={selectedLead?.productMix?.equipment} className="w-full bg-slate-800 border border-slate-600 text-white p-2 rounded focus:border-titan-500 outline-none" />
                            </div>
                            <div>
                                <label className="block text-xs text-slate-500 uppercase font-bold mb-1">Service Split</label>
                                <input name="mixService" type="number" placeholder="Value" defaultValue={selectedLead?.productMix?.service} className="w-full bg-slate-800 border border-slate-600 text-white p-2 rounded focus:border-titan-500 outline-none" />
                            </div>
                            <div>
                                <label className="block text-xs text-slate-500 uppercase font-bold mb-1">GP Margin %</label>
                                <div className="flex items-center gap-2">
                                    <input name="gpMargin" type="number" defaultValue={selectedLead?.gpMargin} className="w-full bg-slate-800 border border-slate-600 text-white p-2 rounded focus:border-titan-500 outline-none" />
                                    {(selectedLead?.gpMargin || 0) < 20 && (
                                        <span title="Low Margin Warning" className="flex items-center">
                                            <AlertTriangle className="w-5 h-5 text-red-500" />
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Next Steps */}
                    <div className="bg-blue-900/10 p-4 rounded border border-blue-900/30">
                        <h4 className="text-blue-400 font-bold text-sm uppercase mb-3">Action Plan</h4>
                        <div className="grid grid-cols-3 gap-4">
                            <div className="col-span-2">
                                <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Next Action Description</label>
                                <input name="nextAction" defaultValue={selectedLead?.nextAction} placeholder="e.g. Call to discuss quote" className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none" />
                            </div>
                            <div>
                                <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Due Date</label>
                                <input name="nextActionDate" type="date" defaultValue={selectedLead?.nextActionDate} className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none" />
                            </div>
                        </div>
                        <div className="mt-3 flex gap-4">
                            <label className="flex items-center gap-2 text-sm text-slate-300">
                                <input type="radio" name="temperature" value="Hot" defaultChecked={selectedLead?.temperature === 'Hot'} /> Hot (Closing Soon)
                            </label>
                            <label className="flex items-center gap-2 text-sm text-slate-300">
                                <input type="radio" name="temperature" value="Warm" defaultChecked={selectedLead?.temperature === 'Warm'} /> Warm (Engaged)
                            </label>
                            <label className="flex items-center gap-2 text-sm text-slate-300">
                                <input type="radio" name="temperature" value="Cold" defaultChecked={selectedLead?.temperature === 'Cold'} /> Cold (Long Term)
                            </label>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
                        <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-slate-400 hover:text-white">Cancel</button>
                        <button type="submit" className="bg-titan-500 hover:bg-titan-600 text-white px-6 py-2 rounded font-bold shadow-lg">Save Opportunity</button>
                    </div>
                </form>
            </Modal>
        )}

        {showActivityModal && (
            <Modal title="Log New Activity" onClose={() => setShowActivityModal(false)}>
                <form onSubmit={handleSaveActivity} className="space-y-4">
                    <div>
                        <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Lead / Company</label>
                        <select name="leadId" className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none">
                            {leads.map(l => <option key={l.id} value={l.id}>{l.company} ({l.contactPerson})</option>)}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Activity Type</label>
                            <select name="type" className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none">
                                <option>Call</option>
                                <option>Site Visit</option>
                                <option>Email</option>
                                <option>Meeting</option>
                                <option>Quote Sent</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Date</label>
                            <input name="date" type="date" required className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none" />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Outcome / Result</label>
                        <input name="outcome" placeholder="e.g. Interested, Scheduled demo" className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none" />
                    </div>
                    <div>
                        <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Notes</label>
                        <textarea name="notes" className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none h-24"></textarea>
                    </div>
                    <button type="submit" className="w-full bg-titan-500 hover:bg-titan-600 text-white font-bold py-2 rounded">Save Activity</button>
                </form>
            </Modal>
        )}

        {showTargetModal && selectedTarget && (
            <Modal title={`Set Targets: ${selectedTarget.repName}`} onClose={() => setShowTargetModal(false)}>
                <form onSubmit={handleSaveTarget} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Call Target (Monthly)</label>
                            <input name="callTarget" type="number" defaultValue={selectedTarget.callTarget} className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none" />
                        </div>
                        <div>
                            <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Visit Target (Monthly)</label>
                            <input name="visitTarget" type="number" defaultValue={selectedTarget.visitTarget} className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Revenue Target (ZAR)</label>
                            <input name="revenueTarget" type="number" defaultValue={selectedTarget.revenueTarget} className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none" />
                        </div>
                        <div>
                            <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Deals Closed Target</label>
                            <input name="dealsTarget" type="number" defaultValue={selectedTarget.dealsClosedTarget} className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none" />
                        </div>
                    </div>
                    <div className="text-xs text-slate-500 mt-2 bg-slate-900 p-3 rounded">
                        Note: Adjusting targets will update the progress bars immediately. Global targets should reflect the sum of all rep targets.
                    </div>
                    <button type="submit" className="w-full bg-titan-500 hover:bg-titan-600 text-white font-bold py-2 rounded mt-4">Update Targets</button>
                </form>
            </Modal>
        )}
    </div>
  );
};

export const HRManagerView: React.FC = () => {
    const [tab, setTab] = useState<'Dashboard' | 'Directory' | 'Leave' | 'Overtime' | 'Training' | 'Performance'>('Dashboard');
    const [employees, setEmployees] = useState<Employee[]>(initialEmployees);
    const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>(initialLeave);
    const [overtimeEntries, setOvertimeEntries] = useState<OvertimeEntry[]>(initialOvertime);
    const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [profileTab, setProfileTab] = useState<'Personal' | 'Employment' | 'Medical' | 'PPE' | 'Competencies' | 'Performance'>('Personal');
    
    // Performance Sub-state
    const [selectedPerformanceReview, setSelectedPerformanceReview] = useState<PerformanceReview | null>(null);

    // --- HELPER FUNCTIONS ---
    
    const handleUpdateReview = (reviewIndex: number, field: keyof PerformanceReview, value: any) => {
        if (!selectedEmployee) return;
        const updatedHistory = [...selectedEmployee.performanceHistory];
        updatedHistory[reviewIndex] = { ...updatedHistory[reviewIndex], [field]: value };
        const updatedEmployee = { ...selectedEmployee, performanceHistory: updatedHistory };
        setSelectedEmployee(updatedEmployee);
        // Also update the main employees list
        setEmployees(employees.map(e => e.id === updatedEmployee.id ? updatedEmployee : e));
    };

    const handleScorecardChange = (reviewIndex: number, itemIndex: number, field: keyof ScorecardItem, value: any) => {
        if (!selectedEmployee) return;
        const currentReview = selectedEmployee.performanceHistory[reviewIndex];
        const updatedScorecard = [...currentReview.scorecard];
        updatedScorecard[itemIndex] = { ...updatedScorecard[itemIndex], [field]: value };
        handleUpdateReview(reviewIndex, 'scorecard', updatedScorecard);
    };

    const handleAddScorecardItem = (reviewIndex: number) => {
        if (!selectedEmployee) return;
        const newScorecardItem: ScorecardItem = {
            id: `SC-${Date.now()}`,
            category: 'New Category',
            criteria: '',
            methodology: '',
            weight: 0,
            target: '',
            actual: '',
            score: 0,
            evidence: []
        };
        const currentReview = selectedEmployee.performanceHistory[reviewIndex];
        const newScorecard = [...currentReview.scorecard, newScorecardItem];
        handleUpdateReview(reviewIndex, 'scorecard', newScorecard);
    };

    const handleDeleteScorecardItem = (reviewIndex: number, itemIndex: number) => {
        if (!selectedEmployee) return;
        const currentReview = selectedEmployee.performanceHistory[reviewIndex];
        const newScorecard = currentReview.scorecard.filter((_, i) => i !== itemIndex);
        handleUpdateReview(reviewIndex, 'scorecard', newScorecard);
    };

    const handlePDPChange = (reviewIndex: number, itemIndex: number, field: keyof PDPGoal, value: any) => {
        if (!selectedEmployee) return;
        const currentReview = selectedEmployee.performanceHistory[reviewIndex];
        const updatedPDP = [...currentReview.pdp];
        updatedPDP[itemIndex] = { ...updatedPDP[itemIndex], [field]: value };
        handleUpdateReview(reviewIndex, 'pdp', updatedPDP);
    };

    const handleAddPDPItem = (reviewIndex: number) => {
        if (!selectedEmployee) return;
        const newGoal: PDPGoal = {
            id: `PDP-${Date.now()}`,
            objective: 'New Goal',
            actionPlan: '',
            targetDate: new Date().toISOString().split('T')[0],
            status: 'Not Started',
            evidence: []
        };
        const currentReview = selectedEmployee.performanceHistory[reviewIndex];
        const newPDP = [...currentReview.pdp, newGoal];
        handleUpdateReview(reviewIndex, 'pdp', newPDP);
    };

    const handleDeletePDPItem = (reviewIndex: number, itemIndex: number) => {
        if (!selectedEmployee) return;
        const currentReview = selectedEmployee.performanceHistory[reviewIndex];
        const newPDP = currentReview.pdp.filter((_, i) => i !== itemIndex);
        handleUpdateReview(reviewIndex, 'pdp', newPDP);
    };

    // --- RENDERERS ---

    const renderDashboard = () => (
        <div className="space-y-6 animate-fade-in">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-slate-800 p-4 rounded border-t-4 border-titan-500">
                    <div className="text-slate-400 text-xs uppercase font-bold">Total Headcount</div>
                    <div className="text-3xl font-bold text-white">{employees.length}</div>
                    <div className="text-green-500 text-xs">Full Time Employees</div>
                </div>
                <div className="bg-slate-800 p-4 rounded border-t-4 border-blue-500">
                    <div className="text-slate-400 text-xs uppercase font-bold">On Leave Today</div>
                    <div className="text-3xl font-bold text-white">
                        {leaveRequests.filter(l => l.status === 'Approved' && new Date(l.startDate) <= new Date() && new Date(l.endDate) >= new Date()).length}
                    </div>
                    <div className="text-slate-500 text-xs">Planned Absence</div>
                </div>
                <div className="bg-slate-800 p-4 rounded border-t-4 border-red-500">
                    <div className="text-slate-400 text-xs uppercase font-bold">Training Expired</div>
                    <div className="text-3xl font-bold text-white">
                        {employees.reduce((acc, emp) => acc + emp.competencies.filter(c => c.status === 'Expired').length, 0)}
                    </div>
                    <div className="text-red-400 text-xs">Action Required</div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
                    <h3 className="text-lg font-bold text-white mb-4">Leave Calendar Summary</h3>
                    <div className="space-y-3">
                        {leaveRequests.map(l => (
                            <div key={l.id} className="flex justify-between items-center bg-slate-900 p-3 rounded border-l-4 border-blue-500">
                                <div>
                                    <div className="text-white font-bold">{employees.find(e => e.id === l.employeeId)?.lastName}</div>
                                    <div className="text-xs text-slate-400">{l.type} • {l.days} Days</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-medium text-slate-300">{l.startDate}</div>
                                    <StatusBadge status={l.status} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
                    <h3 className="text-lg font-bold text-white mb-4">Training Compliance</h3>
                    <div className="space-y-4">
                        {employees.map(emp => {
                            const total = emp.competencies.length;
                            const valid = emp.competencies.filter(c => c.status === 'Valid').length;
                            const pct = total > 0 ? (valid / total) * 100 : 0;
                            return (
                                <div key={emp.id}>
                                    <div className="flex justify-between mb-1 text-sm">
                                        <span className="text-white">{emp.firstName} {emp.lastName}</span>
                                        <span className="text-slate-400">{valid}/{total} Valid</span>
                                    </div>
                                    <ProgressBar current={valid} target={total} color={pct < 100 ? 'bg-red-500' : 'bg-green-500'} />
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>
        </div>
    );

    const renderDirectory = () => (
        <div className="space-y-6 animate-fade-in">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white">Employee Directory</h2>
                <button onClick={() => { setSelectedEmployee(null); setShowProfileModal(true); }} className="bg-titan-500 hover:bg-titan-600 text-white px-4 py-2 rounded font-bold flex items-center gap-2">
                    <UserPlus className="w-4 h-4" /> Add Employee
                </button>
            </div>
            <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
                        <tr>
                            <th className="px-6 py-4">Name</th>
                            <th className="px-6 py-4">Department</th>
                            <th className="px-6 py-4">Position</th>
                            <th className="px-6 py-4">Status</th>
                            <th className="px-6 py-4">Compliance</th>
                            <th className="px-6 py-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                        {employees.map(emp => (
                            <tr key={emp.id} className="hover:bg-slate-750">
                                <td className="px-6 py-4">
                                    <div className="font-bold text-white">{emp.firstName} {emp.lastName}</div>
                                    <div className="text-xs text-slate-500">{emp.email}</div>
                                </td>
                                <td className="px-6 py-4 text-slate-300">{emp.department}</td>
                                <td className="px-6 py-4 text-slate-300">{emp.position}</td>
                                <td className="px-6 py-4"><StatusBadge status={emp.status} /></td>
                                <td className="px-6 py-4">
                                    {emp.competencies.some(c => c.status === 'Expired' && c.requiredForRole) ? 
                                        <span className="text-red-400 text-xs font-bold flex items-center gap-1"><AlertTriangle className="w-3 h-3"/> Non-Compliant</span> :
                                        <span className="text-green-400 text-xs font-bold flex items-center gap-1"><CheckCircle className="w-3 h-3"/> Compliant</span>
                                    }
                                </td>
                                <td className="px-6 py-4">
                                    <button onClick={() => { setSelectedEmployee(emp); setShowProfileModal(true); }} className="text-titan-500 hover:text-white font-medium text-sm">View Profile</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    const renderTrainingMatrix = () => (
        <div className="space-y-6 animate-fade-in">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white">Competency Matrix</h2>
                <div className="flex gap-2">
                    <span className="flex items-center gap-1 text-xs text-slate-400"><div className="w-3 h-3 bg-green-500 rounded-full"></div> Valid</span>
                    <span className="flex items-center gap-1 text-xs text-slate-400"><div className="w-3 h-3 bg-red-500 rounded-full"></div> Expired</span>
                    <span className="flex items-center gap-1 text-xs text-slate-400"><div className="w-3 h-3 bg-slate-700 rounded-full border border-slate-500"></div> Not Required</span>
                </div>
            </div>
            <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
                        <tr>
                            <th className="p-4 border-b border-r border-slate-700 sticky left-0 bg-slate-900 z-10">Employee</th>
                            {['Working at Heights', 'Basic Fire Fighting', 'First Aid L1', 'Forklift License', 'SAMTRAC', 'HIRA'].map(skill => (
                                <th key={skill} className="p-4 border-b border-slate-700 text-center min-w-[100px]">{skill}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {employees.map(emp => (
                            <tr key={emp.id} className="hover:bg-slate-750">
                                <td className="p-4 border-b border-r border-slate-700 font-bold text-white sticky left-0 bg-slate-800">{emp.firstName} {emp.lastName}</td>
                                {['Working at Heights', 'Basic Fire Fighting', 'First Aid L1', 'Forklift License', 'SAMTRAC', 'HIRA'].map(skill => {
                                    const comp = emp.competencies.find(c => c.name === skill);
                                    return (
                                        <td key={skill} className="p-4 border-b border-slate-700 text-center">
                                            {comp ? (
                                                <div className="flex flex-col items-center justify-center">
                                                    <div className={`w-4 h-4 rounded-full ${comp.status === 'Valid' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                                    <span className="text-[10px] text-slate-500 mt-1">{comp.expiryDate}</span>
                                                </div>
                                            ) : (
                                                <div className="w-4 h-4 rounded-full bg-slate-700 border border-slate-600 mx-auto opacity-30"></div>
                                            )}
                                        </td>
                                    )
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <div>
            <div className="border-b border-slate-700 flex gap-6 text-sm mb-6 overflow-x-auto min-h-[4rem] items-center">
                {['Dashboard', 'Directory', 'Leave', 'Overtime', 'Training', 'Performance'].map(t => (
                    <button 
                        key={t} 
                        onClick={() => setTab(t as any)}
                        className={`pb-3 border-b-2 transition flex items-center gap-2 whitespace-nowrap ${tab === t ? 'border-titan-500 text-titan-500' : 'border-transparent text-slate-400 hover:text-white'}`}
                    >
                        {t === 'Training' ? <GraduationCap className="w-4 h-4" /> : t === 'Performance' ? <Award className="w-4 h-4" /> : t === 'Leave' ? <Calendar className="w-4 h-4" /> : <Layout className="w-4 h-4" />}
                        {t === 'Training' ? 'Training Matrix' : t}
                    </button>
                ))}
            </div>

            {tab === 'Dashboard' && renderDashboard()}
            {tab === 'Directory' && renderDirectory()}
            {tab === 'Training' && renderTrainingMatrix()}
            
            {tab === 'Leave' && (
                <div className="space-y-6 animate-fade-in">
                    <div className="flex justify-between items-center">
                        <h2 className="text-xl font-bold text-white">Leave Management</h2>
                        <button className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded font-bold flex items-center gap-2">
                            <Plus className="w-4 h-4" /> Request Leave
                        </button>
                    </div>
                    <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
                        <table className="w-full text-left">
                            <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
                                <tr>
                                    <th className="px-6 py-4">Employee</th>
                                    <th className="px-6 py-4">Type</th>
                                    <th className="px-6 py-4">Dates</th>
                                    <th className="px-6 py-4">Days</th>
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700">
                                {leaveRequests.map(req => (
                                    <tr key={req.id} className="hover:bg-slate-750">
                                        <td className="px-6 py-4 text-white font-medium">{employees.find(e => e.id === req.employeeId)?.lastName}</td>
                                        <td className="px-6 py-4 text-slate-300">{req.type}</td>
                                        <td className="px-6 py-4 text-xs text-slate-400">{req.startDate} to {req.endDate}</td>
                                        <td className="px-6 py-4 text-white">{req.days}</td>
                                        <td className="px-6 py-4"><StatusBadge status={req.status} /></td>
                                        <td className="px-6 py-4">
                                            {req.status === 'Pending' && (
                                                <div className="flex gap-2">
                                                    <button className="text-green-500 hover:text-white text-xs border border-green-500/30 px-2 py-1 rounded">Approve</button>
                                                    <button className="text-red-500 hover:text-white text-xs border border-red-500/30 px-2 py-1 rounded">Reject</button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {tab === 'Overtime' && (
                <div className="space-y-6 animate-fade-in">
                    <div className="flex justify-between items-center">
                        <h2 className="text-xl font-bold text-white">Overtime Approvals</h2>
                        <button className="bg-titan-500 hover:bg-titan-600 text-white px-4 py-2 rounded font-bold flex items-center gap-2">
                            <Clock className="w-4 h-4" /> Log Overtime
                        </button>
                    </div>
                    <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
                        <table className="w-full text-left">
                            <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
                                <tr>
                                    <th className="px-6 py-4">Employee</th>
                                    <th className="px-6 py-4">Date</th>
                                    <th className="px-6 py-4">Hours</th>
                                    <th className="px-6 py-4">Job Link</th>
                                    <th className="px-6 py-4">Reason</th>
                                    <th className="px-6 py-4">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700">
                                {overtimeEntries.map(ot => (
                                    <tr key={ot.id} className="hover:bg-slate-750">
                                        <td className="px-6 py-4 text-white font-medium">{employees.find(e => e.id === ot.employeeId)?.lastName}</td>
                                        <td className="px-6 py-4 text-slate-300">{ot.date}</td>
                                        <td className="px-6 py-4 text-white font-bold">{ot.hours}</td>
                                        <td className="px-6 py-4 font-mono text-titan-400 text-xs">{ot.jobId}</td>
                                        <td className="px-6 py-4 text-slate-400 text-sm truncate max-w-xs">{ot.reason}</td>
                                        <td className="px-6 py-4"><StatusBadge status={ot.status} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {tab === 'Performance' && (
                <div className="space-y-6 animate-fade-in">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
                            <h3 className="text-lg font-bold text-white mb-4">Performance Bell Curve</h3>
                            <div className="h-64 flex items-center justify-center text-slate-500 border border-dashed border-slate-600 rounded">
                                [Performance Distribution Chart Placeholder]
                            </div>
                        </div>
                        <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
                            <h3 className="text-lg font-bold text-white mb-4">Review Cycle Status</h3>
                            <div className="space-y-4">
                                <div className="flex justify-between text-sm text-slate-300">
                                    <span>Goal Setting</span>
                                    <span className="text-green-500">100% Complete</span>
                                </div>
                                <div className="flex justify-between text-sm text-slate-300">
                                    <span>Mid-Year Review</span>
                                    <span className="text-yellow-500">45% Complete</span>
                                </div>
                                <div className="flex justify-between text-sm text-slate-300">
                                    <span>Final Appraisal</span>
                                    <span className="text-slate-500">Not Started</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* --- EMPLOYEE PROFILE MODAL --- */}
            {showProfileModal && (
                <Modal title={selectedEmployee ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}` : "New Employee Onboarding"} onClose={() => setShowProfileModal(false)} size="xl">
                    <div className="flex gap-6 h-[700px]">
                        {/* Sidebar */}
                        <div className="w-64 bg-slate-900 border-r border-slate-700 rounded-l-lg flex flex-col p-4">
                            <div className="text-center mb-6">
                                <div className="w-20 h-20 bg-slate-800 rounded-full mx-auto mb-3 flex items-center justify-center border-2 border-titan-500">
                                    <User className="w-10 h-10 text-titan-500" />
                                </div>
                                {selectedEmployee && (
                                    <>
                                        <h3 className="text-white font-bold">{selectedEmployee.position}</h3>
                                        <div className="text-xs text-slate-500">{selectedEmployee.department}</div>
                                        <div className="mt-2 text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded inline-block">{selectedEmployee.status}</div>
                                    </>
                                )}
                            </div>
                            
                            <div className="space-y-2">
                                {[
                                    { id: 'Personal', icon: User, label: 'Personal & Contact' },
                                    { id: 'Employment', icon: Briefcase, label: 'Employ. & Licenses' },
                                    { id: 'Medical', icon: HeartPulse, label: 'Medical & Safety' },
                                    { id: 'PPE', icon: HardHat, label: 'PPE & Clothing' },
                                    { id: 'Competencies', icon: GraduationCap, label: 'Skills & Training' },
                                    { id: 'Performance', icon: Star, label: 'Performance' },
                                ].map(t => (
                                    <button 
                                        key={t.id}
                                        onClick={() => setProfileTab(t.id as any)}
                                        className={`w-full text-left px-4 py-3 rounded transition flex items-center gap-3 ${profileTab === t.id ? 'bg-slate-800 text-white border-l-4 border-titan-500' : 'text-slate-400 hover:text-white'}`}
                                    >
                                        <t.icon className="w-4 h-4" /> {t.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto pr-2">
                            {profileTab === 'Personal' && (
                                <div className="space-y-6 animate-fade-in">
                                    <h3 className="text-lg font-bold text-white border-b border-slate-700 pb-2">Core Information</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <Input label="First Name" defaultValue={selectedEmployee?.firstName} />
                                        <Input label="Last Name" defaultValue={selectedEmployee?.lastName} />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <Input label="ID / Passport Number" defaultValue={selectedEmployee?.idNumber} required />
                                        <div className="grid grid-cols-2 gap-4">
                                            <Input label="Date of Birth" type="date" defaultValue={selectedEmployee?.dob} />
                                            <div>
                                                <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Gender</label>
                                                <select className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none" defaultValue={selectedEmployee?.gender}>
                                                    <option>Male</option><option>Female</option><option>Other</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <Input label="Nationality" defaultValue={selectedEmployee?.nationality} />
                                        <Input label="Mobile Number" defaultValue={selectedEmployee?.phone} />
                                    </div>
                                    <Input label="Email Address" type="email" defaultValue={selectedEmployee?.email} />
                                    <div className="mb-4">
                                        <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Residential Address</label>
                                        <textarea className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none h-20" defaultValue={selectedEmployee?.address}></textarea>
                                    </div>

                                    <h3 className="text-lg font-bold text-white border-b border-slate-700 pb-2 mt-6">Next of Kin</h3>
                                    {selectedEmployee?.nextOfKin?.map((nok, i) => (
                                        <div key={i} className="bg-slate-900 p-4 rounded border border-slate-700 grid grid-cols-2 gap-4">
                                            <Input label="Full Name" defaultValue={nok.name} />
                                            <Input label="Relationship" defaultValue={nok.relationship} />
                                            <Input label="Contact Number" defaultValue={nok.phone} />
                                            <Input label="Address" defaultValue={nok.address} />
                                        </div>
                                    ))}
                                    <button className="text-xs bg-slate-700 hover:bg-slate-600 text-white px-3 py-1 rounded">+ Add Next of Kin</button>
                                </div>
                            )}

                            {profileTab === 'Employment' && (
                                <div className="space-y-6 animate-fade-in">
                                    <h3 className="text-lg font-bold text-white border-b border-slate-700 pb-2">Job & Contract Details</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <Input label="Job Title / Position" defaultValue={selectedEmployee?.position} />
                                        <div>
                                            <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Department</label>
                                            <select className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none" defaultValue={selectedEmployee?.department}>
                                                {['Operations', 'Sales', 'Finance', 'HR', 'HSSE', 'Workshop'].map(d => <option key={d} value={d}>{d}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <Input label="Start Date" type="date" defaultValue={selectedEmployee?.startDate} />
                                        <div>
                                            <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Employment Type</label>
                                            <select className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none" defaultValue={selectedEmployee?.employmentType}>
                                                <option>Permanent</option><option>Fixed-Term</option><option>Contractor</option>
                                            </select>
                                        </div>
                                    </div>
                                    <Input label="Reporting Manager" defaultValue={selectedEmployee?.managerName} />

                                    <h3 className="text-lg font-bold text-white border-b border-slate-700 pb-2 mt-6 flex justify-between">
                                        <span>Driver's Licence & Authorisations</span>
                                        <span className="text-xs font-normal text-slate-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3"/> Expiry Alerts Active</span>
                                    </h3>
                                    <div className="bg-slate-900 p-4 rounded border border-slate-700 space-y-4">
                                        <div className="grid grid-cols-3 gap-4">
                                            <Input label="Licence Code" defaultValue={selectedEmployee?.driversLicense?.code} placeholder="e.g. C1" />
                                            <Input label="Licence Number" defaultValue={selectedEmployee?.driversLicense?.number} />
                                            <Input label="Issue Date" type="date" defaultValue={selectedEmployee?.driversLicense?.issueDate} />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="relative">
                                                <Input label="Expiry Date" type="date" defaultValue={selectedEmployee?.driversLicense?.expiryDate} className={new Date(selectedEmployee?.driversLicense?.expiryDate || '') < new Date() ? 'border-red-500 text-red-500' : ''} />
                                                {new Date(selectedEmployee?.driversLicense?.expiryDate || '') < new Date() && <span className="absolute right-0 top-0 text-xs text-red-500 font-bold">EXPIRED</span>}
                                            </div>
                                            <Input label="PrDP Expiry (if applicable)" type="date" defaultValue={selectedEmployee?.driversLicense?.prdpExpiry} />
                                        </div>
                                        <div className="flex justify-between items-center pt-2 border-t border-slate-800">
                                            <div className="text-xs text-slate-500">Upload Copy of Licence</div>
                                            <button className="flex items-center gap-2 text-blue-400 hover:text-white text-xs"><Paperclip className="w-3 h-3" /> Upload Document</button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {profileTab === 'Medical' && (
                                <div className="space-y-6 animate-fade-in">
                                    <div className="flex justify-between items-center border-b border-slate-700 pb-2">
                                        <h3 className="text-lg font-bold text-white">Medical Surveillance</h3>
                                        <div className="flex items-center gap-2 text-xs text-red-400 border border-red-900/50 bg-red-900/10 px-2 py-1 rounded">
                                            <Lock className="w-3 h-3" /> Confidential Access
                                        </div>
                                    </div>
                                    
                                    <div className="bg-slate-900 p-4 rounded border border-slate-700">
                                        <div className="flex justify-between mb-4">
                                            <div>
                                                <label className="text-xs text-slate-500 uppercase font-bold">Fitness Status</label>
                                                <div className="mt-1">
                                                    <StatusBadge status={selectedEmployee?.medical?.status || 'Unknown'} />
                                                </div>
                                            </div>
                                            <button className="text-xs bg-slate-700 hover:bg-slate-600 text-white px-3 py-1 rounded h-fit">Update Status</button>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4 mb-4">
                                            <Input label="Examination Date" type="date" defaultValue={selectedEmployee?.medical?.examDate} />
                                            <Input label="Certificate Expiry" type="date" defaultValue={selectedEmployee?.medical?.expiryDate} />
                                        </div>
                                        <div className="mb-4">
                                            <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Medical Restrictions / Notes</label>
                                            <textarea className="w-full bg-slate-800 border border-slate-600 text-white p-2 rounded focus:border-titan-500 outline-none h-16" defaultValue={selectedEmployee?.medical?.restrictions}></textarea>
                                        </div>
                                        <div className="flex items-center gap-2 text-blue-400 hover:text-white cursor-pointer text-sm">
                                            <FileCheck className="w-4 h-4" /> View Medical Certificate
                                        </div>
                                    </div>

                                    <h3 className="text-lg font-bold text-white border-b border-slate-700 pb-2 mt-6">Health & Safety Info</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Known Allergies</label>
                                            <input className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none" defaultValue={selectedEmployee?.medical?.allergies} placeholder="e.g. Penicillin, Bee Stings" />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Emergency Instructions</label>
                                            <input className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none" defaultValue={selectedEmployee?.medical?.emergencyInstructions} />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {profileTab === 'PPE' && (
                                <div className="space-y-6 animate-fade-in">
                                    <h3 className="text-lg font-bold text-white border-b border-slate-700 pb-2">Clothing Sizes</h3>
                                    <div className="grid grid-cols-3 gap-4 bg-slate-900 p-4 rounded border border-slate-700">
                                        <Input label="Shirt Size" defaultValue={selectedEmployee?.clothingSizes?.shirt} className="text-center font-bold" />
                                        <Input label="Trousers Size" defaultValue={selectedEmployee?.clothingSizes?.trousers} className="text-center font-bold" />
                                        <Input label="Overalls" defaultValue={selectedEmployee?.clothingSizes?.overalls} className="text-center font-bold" />
                                        <Input label="Safety Shoes" defaultValue={selectedEmployee?.clothingSizes?.safetyShoes} className="text-center font-bold" />
                                        <Input label="Jacket / Winter" defaultValue={selectedEmployee?.clothingSizes?.jacket} className="text-center font-bold" />
                                        <Input label="Gloves" defaultValue={selectedEmployee?.clothingSizes?.gloves} className="text-center font-bold" />
                                    </div>

                                    <div className="flex justify-between items-center border-b border-slate-700 pb-2 mt-6">
                                        <h3 className="text-lg font-bold text-white">Issued PPE Register</h3>
                                        <button className="text-xs bg-titan-500 hover:bg-titan-600 text-white px-3 py-1 rounded font-bold">+ Issue New</button>
                                    </div>
                                    <div className="space-y-3">
                                        {selectedEmployee?.ppeIssued.map((item, i) => (
                                            <div key={i} className="bg-slate-900 p-4 rounded border border-slate-700 flex justify-between items-center">
                                                <div>
                                                    <div className="text-white font-bold">{item.item}</div>
                                                    <div className="text-xs text-slate-500">Issued: {item.issueDate}</div>
                                                </div>
                                                <div className="text-right">
                                                    <StatusBadge status={item.status} />
                                                    <div className="text-xs text-slate-500 mt-1">Exp: {item.expiryDate}</div>
                                                </div>
                                            </div>
                                        ))}
                                        {(!selectedEmployee?.ppeIssued || selectedEmployee.ppeIssued.length === 0) && <div className="text-slate-500 italic">No PPE recorded.</div>}
                                    </div>
                                </div>
                            )}

                            {profileTab === 'Competencies' && (
                                <div className="space-y-6 animate-fade-in">
                                    <div className="flex justify-between items-center border-b border-slate-700 pb-2">
                                        <h3 className="text-lg font-bold text-white">Skills & Certifications</h3>
                                        <button className="text-xs bg-titan-500 hover:bg-titan-600 text-white px-3 py-1 rounded font-bold">+ Add Skill</button>
                                    </div>
                                    <div className="space-y-3">
                                        {selectedEmployee?.competencies.map((comp, i) => (
                                            <div key={i} className="bg-slate-900 p-4 rounded border border-slate-700">
                                                <div className="flex justify-between items-start mb-2">
                                                    <span className="text-white font-bold">{comp.name}</span>
                                                    <StatusBadge status={comp.status} />
                                                </div>
                                                <div className="flex justify-between text-xs text-slate-400 mb-2">
                                                    <span>Issued: {comp.issueDate}</span>
                                                    <span>Expires: {comp.expiryDate}</span>
                                                </div>
                                                {comp.documents && comp.documents.length > 0 ? (
                                                    <div className="flex flex-col gap-1 mt-2 bg-slate-800 p-2 rounded">
                                                        {comp.documents.map((doc, j) => (
                                                            <div key={j} className="text-xs flex items-center gap-2 text-blue-400 hover:underline cursor-pointer">
                                                                <Paperclip className="w-3 h-3" /> {doc.name}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="text-xs text-slate-500 italic mt-2">No certificates uploaded.</div>
                                                )}
                                                <div className="mt-2 flex justify-between items-center">
                                                    {comp.requiredForRole && <div className="text-xs text-titan-500 font-bold uppercase">Required For Role</div>}
                                                    <button className="text-xs border border-slate-600 hover:bg-slate-700 text-white px-2 py-1 rounded flex items-center gap-1">
                                                        <Upload className="w-3 h-3" /> Upload Cert
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {profileTab === 'Performance' && (
                                <div className="space-y-8 animate-fade-in">
                                    {selectedEmployee?.performanceHistory.map((review, i) => (
                                        <div key={i} className="space-y-6 border-l-2 border-titan-500 pl-4">
                                            <div className="flex justify-between items-center bg-slate-900 p-4 rounded border border-slate-700">
                                                <div>
                                                    <h3 className="text-lg font-bold text-white">{review.period} Review Cycle</h3>
                                                    <div className="text-xs text-slate-400">Review Date: {review.date} • Reviewer: {review.reviewer}</div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-2xl font-bold text-titan-500">{review.rating}<span className="text-sm text-slate-500">/5</span></div>
                                                    <div className="text-xs text-slate-400">Final Rating</div>
                                                </div>
                                            </div>

                                            {/* Documents Section */}
                                            <div className="bg-slate-800 p-4 rounded border border-slate-700">
                                                <h4 className="text-sm font-bold text-white uppercase mb-3 flex items-center gap-2"><FileCheck className="w-4 h-4"/> Signed Reviews & Appraisals</h4>
                                                <div className="space-y-2">
                                                    {review.documents && review.documents.length > 0 ? review.documents.map((doc, d) => (
                                                        <div key={d} className="flex justify-between items-center bg-slate-900 p-2 rounded border border-slate-700">
                                                            <div className="flex items-center gap-2 text-sm text-blue-400">
                                                                <FileText className="w-4 h-4" /> {doc.name}
                                                            </div>
                                                            <div className="text-xs text-slate-500">{doc.uploadDate}</div>
                                                        </div>
                                                    )) : <div className="text-xs text-slate-500 italic">No signed documents uploaded yet.</div>}
                                                    <button className="mt-2 text-xs bg-slate-700 hover:bg-slate-600 text-white px-3 py-1 rounded flex items-center gap-2 w-fit">
                                                        <Upload className="w-3 h-3" /> Upload Signed Appraisal
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Scorecard Section */}
                                            <div className="bg-slate-800 p-4 rounded border border-slate-700">
                                                <div className="flex justify-between items-center mb-3">
                                                    <h4 className="text-sm font-bold text-white uppercase flex items-center gap-2"><Target className="w-4 h-4"/> Performance Scorecard</h4>
                                                </div>
                                                <div className="overflow-x-auto">
                                                    <table className="w-full text-left text-xs">
                                                        <thead className="bg-slate-900 text-slate-400 uppercase">
                                                            <tr>
                                                                <th className="p-2">Category</th>
                                                                <th className="p-2">Criteria (What)</th>
                                                                <th className="p-2">Methodology (How)</th>
                                                                <th className="p-2 w-16">Target</th>
                                                                <th className="p-2 w-16">Actual</th>
                                                                <th className="p-2 w-12">Score</th>
                                                                <th className="p-2 w-20">Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-slate-700 text-slate-300">
                                                            {review.scorecard.map((item, s) => (
                                                                <tr key={s}>
                                                                    <td className="p-2 font-bold text-titan-500">
                                                                        <input className="bg-transparent w-full outline-none focus:text-white" value={item.category} onChange={(e) => handleScorecardChange(i, s, 'category', e.target.value)} />
                                                                    </td>
                                                                    <td className="p-2">
                                                                        <input className="bg-transparent w-full outline-none focus:text-white" value={item.criteria} onChange={(e) => handleScorecardChange(i, s, 'criteria', e.target.value)} />
                                                                    </td>
                                                                    <td className="p-2 text-slate-400 italic">
                                                                        <input className="bg-transparent w-full outline-none focus:text-white italic" value={item.methodology} onChange={(e) => handleScorecardChange(i, s, 'methodology', e.target.value)} />
                                                                    </td>
                                                                    <td className="p-2">
                                                                        <input className="bg-transparent w-full outline-none focus:text-white" value={item.target} onChange={(e) => handleScorecardChange(i, s, 'target', e.target.value)} />
                                                                    </td>
                                                                    <td className="p-2 text-white font-bold">
                                                                        <input className="bg-transparent w-full outline-none focus:text-white" value={item.actual} onChange={(e) => handleScorecardChange(i, s, 'actual', e.target.value)} />
                                                                    </td>
                                                                    <td className="p-2">
                                                                        <input className="bg-slate-700 px-1 py-0.5 rounded text-white font-bold w-8 text-center outline-none" value={item.score} onChange={(e) => handleScorecardChange(i, s, 'score', e.target.value)} />
                                                                    </td>
                                                                    <td className="p-2 flex gap-2">
                                                                        <button className="text-blue-400 hover:underline flex items-center gap-1"><LinkIcon className="w-3 h-3"/></button>
                                                                        <button onClick={() => handleDeleteScorecardItem(i, s)} className="text-red-400 hover:text-red-300"><Trash2 className="w-3 h-3"/></button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                    <button onClick={() => handleAddScorecardItem(i)} className="mt-2 text-xs bg-slate-700 hover:bg-slate-600 text-white px-3 py-1 rounded flex items-center gap-2">
                                                        <Plus className="w-3 h-3" /> Add Criteria
                                                    </button>
                                                </div>
                                            </div>

                                            {/* PDP Section */}
                                            <div className="bg-slate-800 p-4 rounded border border-slate-700">
                                                <div className="flex justify-between items-center mb-3">
                                                    <h4 className="text-sm font-bold text-white uppercase flex items-center gap-2"><TrendingUp className="w-4 h-4"/> Personal Development Plan (PDP)</h4>
                                                </div>
                                                <div className="space-y-3">
                                                    {review.pdp.map((goal, p) => (
                                                        <div key={p} className="bg-slate-900 p-3 rounded border border-slate-700 group relative">
                                                            <button onClick={() => handleDeletePDPItem(i, p)} className="absolute top-2 right-2 text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition">
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                            <div className="flex justify-between items-start mb-2 pr-6">
                                                                <div className="font-bold text-white text-sm w-full">
                                                                    <input className="bg-transparent w-full outline-none focus:text-titan-500 font-bold" value={goal.objective} onChange={(e) => handlePDPChange(i, p, 'objective', e.target.value)} />
                                                                </div>
                                                                <StatusBadge status={goal.status} />
                                                            </div>
                                                            <div className="text-xs text-slate-400 mb-2 w-full">
                                                                <span className="font-bold text-slate-500 uppercase block mb-1">Action Plan:</span> 
                                                                <textarea className="bg-transparent w-full outline-none focus:text-white h-12 text-slate-300 resize-none border border-transparent focus:border-slate-700 rounded p-1" value={goal.actionPlan} onChange={(e) => handlePDPChange(i, p, 'actionPlan', e.target.value)} />
                                                            </div>
                                                            <div className="flex justify-between items-center text-xs border-t border-slate-800 pt-2 mt-2">
                                                                <span className="text-titan-500 flex items-center gap-1">Target: <input type="date" className="bg-transparent text-titan-500 outline-none w-24" value={goal.targetDate} onChange={(e) => handlePDPChange(i, p, 'targetDate', e.target.value)} /></span>
                                                                <button className="text-slate-400 hover:text-white flex items-center gap-1"><Paperclip className="w-3 h-3"/> Attach Evidence</button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    <button onClick={() => handleAddPDPItem(i)} className="text-xs bg-slate-700 hover:bg-slate-600 text-white px-3 py-1 rounded flex items-center gap-2">
                                                        <Plus className="w-3 h-3" /> Add Development Goal
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {(!selectedEmployee?.performanceHistory || selectedEmployee.performanceHistory.length === 0) && (
                                        <div className="text-center p-8 text-slate-500 italic border border-dashed border-slate-700 rounded">
                                            No performance reviews on record.
                                            <button className="block mx-auto mt-2 text-titan-500 hover:text-white text-sm font-bold">Initiate New Review Cycle</button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    
                    <div className="flex justify-end gap-3 pt-4 border-t border-slate-700 mt-4">
                        <button onClick={() => setShowProfileModal(false)} className="px-4 py-2 text-slate-400 hover:text-white">Close</button>
                        <button className="bg-titan-500 hover:bg-titan-600 text-white px-6 py-2 rounded font-bold">Save Changes</button>
                    </div>
                </Modal>
            )}
        </div>
    )
}

export const ExpenseTrackerView: React.FC = () => {
    const [view, setView] = useState<'List' | 'Dashboard'>('List');
    const [expenses, setExpenses] = useState<ExpenseTransaction[]>(initialExpenses);
    const [showModal, setShowModal] = useState(false);

    const handleSaveExpense = (e: React.FormEvent) => {
        e.preventDefault();
        const formData = new FormData(e.target as HTMLFormElement);
        const newExpense: ExpenseTransaction = {
            id: `EXP-${Date.now()}`,
            date: formData.get('date') as string,
            merchant: formData.get('merchant') as string,
            amount: Number(formData.get('amount')),
            currency: 'ZAR',
            category: formData.get('category') as any,
            paymentMethod: formData.get('paymentMethod') as any,
            description: formData.get('description') as string,
            isBillable: formData.get('isBillable') === 'on',
            status: 'Draft',
            submittedBy: 'Current User'
        };
        setExpenses([newExpense, ...expenses]);
        setShowModal(false);
    }

    return (
        <div className="animate-fade-in">
            <div className="flex justify-between items-center mb-6 min-h-[4rem]">
                <h2 className="text-xl font-bold text-white">Expense Tracker</h2>
                <div className="flex gap-2">
                     <button onClick={() => setView('Dashboard')} className={`px-4 py-2 rounded ${view==='Dashboard'?'bg-slate-700 text-white':'text-slate-400'}`}>Dashboard</button>
                     <button onClick={() => setView('List')} className={`px-4 py-2 rounded ${view==='List'?'bg-slate-700 text-white':'text-slate-400'}`}>Transactions</button>
                     <button onClick={() => setShowModal(true)} className="bg-titan-500 hover:bg-titan-600 text-white px-4 py-2 rounded font-bold flex items-center gap-2">
                        <Plus className="w-4 h-4" /> Add Expense
                    </button>
                </div>
            </div>

            {view === 'List' && (
                <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
                    <table className="w-full text-left">
                        <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
                            <tr>
                                <th className="px-6 py-4">Date</th>
                                <th className="px-6 py-4">Merchant</th>
                                <th className="px-6 py-4">Category</th>
                                <th className="px-6 py-4">Amount</th>
                                <th className="px-6 py-4">Status</th>
                                <th className="px-6 py-4">Billable</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700">
                            {expenses.map(exp => (
                                <tr key={exp.id} className="hover:bg-slate-750">
                                    <td className="px-6 py-4 text-slate-300">{exp.date}</td>
                                    <td className="px-6 py-4 text-white font-medium">{exp.merchant}</td>
                                    <td className="px-6 py-4 text-slate-300">{exp.category}</td>
                                    <td className="px-6 py-4 text-white font-bold">R {exp.amount.toFixed(2)}</td>
                                    <td className="px-6 py-4"><StatusBadge status={exp.status} /></td>
                                    <td className="px-6 py-4">{exp.isBillable ? <CheckCircle className="w-4 h-4 text-green-500"/> : <span className="text-slate-600">-</span>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
             
            {view === 'Dashboard' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-slate-800 p-6 rounded border border-slate-700">
                        <h3 className="text-lg font-bold text-white mb-4">Spend by Category</h3>
                        <div className="h-64">
                             <ResponsiveContainer width="100%" height="100%">
                                <RePieChart>
                                    <Pie data={categoryData} innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                                        {categoryData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip contentStyle={{backgroundColor: '#1e293b', borderColor: '#334155'}} />
                                </RePieChart>
                             </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            )}

            {showModal && (
                <Modal title="Log New Expense" onClose={() => setShowModal(false)}>
                    <form onSubmit={handleSaveExpense} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <Input label="Date" name="date" type="date" required />
                            <Input label="Merchant" name="merchant" required />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <Input label="Amount (ZAR)" name="amount" type="number" step="0.01" required />
                            <div>
                                <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Category</label>
                                <select name="category" className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none">
                                    <option>Fuel</option><option>Travel</option><option>Accommodation</option><option>Meals</option><option>Parts/Spares</option><option>Office Supplies</option>
                                </select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                             <div>
                                <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Payment Method</label>
                                <select name="paymentMethod" className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none">
                                    <option>Company Card</option><option>Cash (Pocket)</option><option>EFT</option>
                                </select>
                            </div>
                            <div className="flex items-center mt-6">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" name="isBillable" className="w-4 h-4 rounded bg-slate-800 border-slate-600 text-titan-500" />
                                    <span className="text-sm text-slate-300">Billable to Customer?</span>
                                </label>
                            </div>
                        </div>
                        <Input label="Description" name="description" />
                        <div className="flex justify-end pt-4">
                            <button type="submit" className="bg-titan-500 hover:bg-titan-600 text-white px-6 py-2 rounded font-bold">Save Expense</button>
                        </div>
                    </form>
                </Modal>
            )}
        </div>
    )
}

export const ContractorMgrView: React.FC = () => {
    const [contractors, setContractors] = useState<Contractor[]>(initialContractors);
    const [selectedContractor, setSelectedContractor] = useState<Contractor | null>(null);
    const [showModal, setShowModal] = useState(false);

    return (
        <div className="animate-fade-in">
             {/* List View */}
             {!selectedContractor && (
                 <>
                    <div className="flex justify-between items-center mb-6 min-h-[4rem]">
                        <h2 className="text-xl font-bold text-white">Contractor Management</h2>
                        <button onClick={() => setShowModal(true)} className="bg-titan-500 hover:bg-titan-600 text-white px-4 py-2 rounded font-bold flex items-center gap-2">
                            <Plus className="w-4 h-4" /> Onboard Contractor
                        </button>
                    </div>
                    <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
                        <table className="w-full text-left">
                            <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
                                <tr>
                                    <th className="px-6 py-4">Company Name</th>
                                    <th className="px-6 py-4">Trades</th>
                                    <th className="px-6 py-4">Grade</th>
                                    <th className="px-6 py-4">Risk Level</th>
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700">
                                {contractors.map(c => (
                                    <tr key={c.id} className="hover:bg-slate-750">
                                        <td className="px-6 py-4">
                                            <div className="text-white font-bold">{c.name}</div>
                                            <div className="text-xs text-slate-500">{c.contactPerson}</div>
                                        </td>
                                        <td className="px-6 py-4 text-slate-300">{c.trades.join(', ')}</td>
                                        <td className="px-6 py-4 text-white font-bold">{c.grade}</td>
                                        <td className="px-6 py-4"><span className={`px-2 py-1 rounded text-xs font-bold ${c.riskLevel === 'High' ? 'bg-red-500 text-white' : c.riskLevel === 'Medium' ? 'bg-orange-500 text-white' : 'bg-green-500 text-white'}`}>{c.riskLevel}</span></td>
                                        <td className="px-6 py-4"><StatusBadge status={c.status} /></td>
                                        <td className="px-6 py-4">
                                            <button onClick={() => setSelectedContractor(c)} className="text-titan-500 hover:text-white text-sm font-medium">Manage</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                 </>
             )}

             {/* Detail View logic would go here if needed, or stick to simple list for now as per error fix requirement */}
             {selectedContractor && (
                 <div className="animate-fade-in">
                    <div className="flex justify-between items-center mb-6 min-h-[4rem]">
                        <button onClick={() => setSelectedContractor(null)} className="text-slate-400 hover:text-white flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Back to Contractors</button>
                    </div>
                     <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
                         <div className="flex justify-between items-start mb-6">
                             <div>
                                 <h2 className="text-2xl font-bold text-white">{selectedContractor.name}</h2>
                                 <div className="text-slate-400">{selectedContractor.tradingName} • {selectedContractor.regNumber}</div>
                             </div>
                             <StatusBadge status={selectedContractor.status} />
                         </div>
                         
                         {/* Simple tabs or sections for documents/employees */}
                         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                             <div className="bg-slate-900 p-4 rounded border border-slate-700">
                                 <h3 className="text-white font-bold mb-3 border-b border-slate-800 pb-2">Compliance Documents</h3>
                                 <div className="space-y-2">
                                     {selectedContractor.documents.map(d => (
                                         <div key={d.id} className="flex justify-between items-center text-sm">
                                             <span className="text-slate-300">{d.type}</span>
                                             <div className="flex items-center gap-2">
                                                 <span className="text-slate-500 text-xs">{d.expiryDate}</span>
                                                 <StatusBadge status={d.status} />
                                             </div>
                                         </div>
                                     ))}
                                 </div>
                             </div>
                             
                              <div className="bg-slate-900 p-4 rounded border border-slate-700">
                                 <h3 className="text-white font-bold mb-3 border-b border-slate-800 pb-2">Approved Employees</h3>
                                 <div className="space-y-2">
                                     {selectedContractor.employees.map(e => (
                                         <div key={e.id} className="flex justify-between items-center text-sm">
                                             <span className="text-slate-300">{e.name}</span>
                                             <span className="text-slate-500 text-xs">{e.role}</span>
                                         </div>
                                     ))}
                                 </div>
                             </div>
                         </div>
                     </div>
                 </div>
             )}

             {showModal && (
                 <Modal title="Onboard New Contractor" onClose={() => setShowModal(false)}>
                     <form className="space-y-4">
                         <div className="grid grid-cols-2 gap-4">
                             <Input label="Company Name" name="name" required />
                             <Input label="Registration Number" name="reg" required />
                         </div>
                         <Input label="Contact Person" name="contact" required />
                         <div className="grid grid-cols-2 gap-4">
                             <Input label="Email" name="email" type="email" required />
                             <Input label="Phone" name="phone" required />
                         </div>
                         <div className="flex justify-end pt-4">
                             <button type="submit" className="bg-titan-500 hover:bg-titan-600 text-white px-6 py-2 rounded font-bold">Save Contractor</button>
                         </div>
                     </form>
                 </Modal>
             )}
        </div>
    )
}

const ClockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
);
